import DataTable from 'datatables.net-bs5';
import * as Highcharts from 'highcharts';
import * as bootstrap from 'bootstrap';
import { getChartTheme } from './utils/chartTheme';
declare var sv_gained_data: Record<string, Record<string, number | null>> | undefined;

export default class {
    static init = () => {
        if (typeof sv_gained_data === 'undefined') return;
        if (!document.querySelector('[data-gained-chart]')) return;

        initSortPlugin();
        initTable();
        const chart = initChart(sv_gained_data);
        wireSelector(chart, sv_gained_data);
        wireTabHashSync(chart);
    }
}

function initSortPlugin(): void {
    // '—' rows sort to bottom in BOTH directions per D-06. DT 2.x sinks null.
    // If UAT proves otherwise, swap to per-direction `gained-asc`/`gained-desc` sort
    // functions (RESEARCH Open Question #2 / Pattern 7 fallback).
    (DataTable as any).ext.type.order['gained-pre'] = (d: string) => {
        if (d === '' || d == null) return null;
        return Number(d);
    };
}

function initTable(): void {
    const el = document.querySelector('.datatable[data-table="sv-gained"]') as HTMLTableElement | null;
    if (!el) return;
    new DataTable(el, {
        paging: false,
        searching: false,
        info: false,
        lengthChange: false,
        order: [],
        columnDefs: [{ targets: [1, 2, 3, 4, 5], type: 'gained' }],
    });
}

function initChart(data: Record<string, Record<string, number | null>>): Highcharts.Chart {
    const container = document.querySelector('[data-gained-chart]') as HTMLElement;

    const skills = ['farming', 'mining', 'foraging', 'fishing', 'combat'];
    const labels = ['🌾 Farming', '⛏️ Mining', '🌲 Foraging', '🎣 Fishing', '⚔️ Combat'];
    const colors = ['#6aa84f', '#999999', '#3d9970', '#339af0', '#e03131'];
    const seriesData = skills.map((s, i) => ({ y: data[s]?.['24h'] ?? 0, color: colors[i] }));

    return Highcharts.chart(container, Highcharts.merge(getChartTheme(), {
        chart: { type: 'column', height: 320 },
        title: { text: 'XP Gained — 24h', style: { fontSize: '14px' } },
        xAxis: { categories: labels },
        yAxis: { title: { text: 'XP' }, gridLineWidth: 0.1 },
        legend: { enabled: false },
        credits: { enabled: false },
        series: [{ type: 'column', name: 'XP Gained', data: seriesData, colorByPoint: true }],
    }));
}

function wireTabHashSync(chart: Highcharts.Chart): void {
    const triggers = document.querySelectorAll<HTMLButtonElement>('[data-bs-toggle="tab"][data-bs-target^="#tab-sv-farmer-"]');
    if (triggers.length === 0) return;

    const slugOf = (target: string) => target.replace(/^#tab-sv-farmer-/, '');
    const triggerFor = (slug: string) =>
        Array.from(triggers).find(b => slugOf(b.getAttribute('data-bs-target') || '') === slug);

    triggers.forEach(btn => {
        btn.addEventListener('shown.bs.tab', () => {
            const slug = slugOf(btn.getAttribute('data-bs-target') || '');
            history.replaceState(null, '', '#' + slug);
            if (slug === 'gained') chart.reflow();
        });
    });

    const initialSlug = (location.hash || '').replace(/^#/, '');
    if (initialSlug) {
        const target = triggerFor(initialSlug);
        if (target) bootstrap.Tab.getOrCreateInstance(target).show();
    }
}

function wireSelector(chart: Highcharts.Chart, data: Record<string, Record<string, number | null>>): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>('[data-gained-period]');
    buttons.forEach(btn => {
        btn.addEventListener('click', () => {
            buttons.forEach(b => {
                b.classList.remove('btn-secondary', 'active');
                b.classList.add('btn-outline-secondary');
            });
            btn.classList.remove('btn-outline-secondary');
            btn.classList.add('btn-secondary', 'active');

            const period = btn.getAttribute('data-gained-period')!;
            const skills = ['farming', 'mining', 'foraging', 'fishing', 'combat'];
            const colors = ['#6aa84f', '#999999', '#3d9970', '#339af0', '#e03131'];
            chart.series[0].setData(skills.map((s, i) => ({ y: data[s]?.[period] ?? 0, color: colors[i] })));
            chart.setTitle({ text: 'XP Gained — ' + period });
        });
    });
}
